import './styles.scss';

import { ComponentProps, ReactElement, cloneElement, useCallback, useMemo, useState } from 'react';
import InvoicePdfTableRow, { SubtotalRow } from '@/features/PdfLayout/layouts/Invoice/InvoicePdfTable/InvoicePdfTableRow';
import { InvoiceTripCombineTypeEnum, InvoiceTripTypeEnum, RunPreviewInvoiceResponse } from '@/models/gen/graphql';

import InvoicePdfReadyContext from '@/features/PdfLayout/layouts/Invoice/InvoicePdfTable/InvoicePdfReadyContext';
import InvoicePdfTableHeader from '@/features/PdfLayout/layouts/Invoice/InvoicePdfTable/InvoicePdfTableHeader';
import Table from '@/common/Table';
import { getClasses } from '@/utils';
import { usePdfLayout } from '@/features/PdfLayout';

export type InvoiceTableProps = ComponentProps<typeof Table> & {
  combineIds?: string[];
  requiresNames?: boolean;
  showCrewId?: boolean;
};

const ILLEGAL_COMBINE_TYPES: InvoiceTripCombineTypeEnum[] = [
  InvoiceTripCombineTypeEnum.Illegal,
  InvoiceTripCombineTypeEnum.IllegalOverCapacity,
];

const InvoicePdfTable = ({ combineIds, requiresNames, showCrewId, ...props }: InvoiceTableProps): ReactElement => {
  const { hasDiscounts, trips, types, totalTrips, netBillableTrips, subtotals, customCharges, error } = usePdfLayout(
    ({ state }: { state: { data: RunPreviewInvoiceResponse; error?: unknown } }) => ({
      trips: state?.data?.output?.trips,
      grandSubtotal: state?.data?.output?.invoice?.grandSubtotal,
      grandTotal: state?.data?.output?.invoice?.grandTotal,
      totalSavings: state?.data?.output?.invoice?.totalSavings,
      hasDiscounts: state?.data?.output?.invoice?.hasDiscounts,
      items: state?.data?.output?.items,
      totalTrips: state?.data?.output?.invoice?.totalTripsBilled || state?.data?.output?.trips?.length || 0,
      netBillableTrips: state?.data?.output?.invoice?.netBillableTrips,
      types: (state?.data?.output?.types.map((type) => type.type) as unknown as InvoiceTripTypeEnum[]) || [],
      subtotals: state?.data?.subtotals,
      customCharges: state?.data?.output?.customCharges,
      error: state?.error,
    })
  );
  const [combinedGroupsRendered, setCombinedGroupsRendered] = useState(0);
  const notifyCombinedGroupRendered = useCallback(() => setCombinedGroupsRendered((n) => n + 1), []);

  // Group combined trips so we can apply combine tag style to it
  const [groupedRows, totalCombinedGroups] = useMemo(() => {
    const result = [];
    let i = 0;
    let subtotalIndex = 0;
    let tripsProcessed = 0;
    let combinedGroupCount = 0;

    while (i < trips?.length) {
      const trip = trips[i];

      const combineId = trip?.combineId;
      const isIllegalCombine = ILLEGAL_COMBINE_TYPES.includes(trip?.combineType);
      if (combineId && !isIllegalCombine && hasDiscounts === 1) {
        // Scan forward to find the end of this group
        const combineIndex = combineIds?.findIndex((id) => id === trip?.combineId);
        let groupEnd = i + 1;
        for (; groupEnd < trips.length; groupEnd++) {
          const next = trips[groupEnd];
          if (next?.combineId !== combineId) break;
        }
        const groupLength = groupEnd - i;
        if (groupLength > 1) {
          result.push(
            <Table.Row key={combineId}>
              <Table.Cell className="COMBINE">
                <span className="CombineTag">
                  <span>Combined</span>
                </span>
                <Table>
                  {trips.slice(i, groupEnd).map((groupTrip, idx) => (
                    <InvoicePdfTableRow
                      key={`${i}-${idx}`}
                      types={types}
                      trip={groupTrip}
                      hasDiscounts={hasDiscounts}
                      className={idx === groupLength - 1 ? 'LastCombinedRow' : undefined}
                      virtualize={false}
                      style={{
                        backgroundColor:
                          combineIndex !== -1 && hasDiscounts === 1 && trip?.combineId
                            ? (combineIndex + 1) % 2 === 0
                              ? '#e3f9ff'
                              : '#effcff'
                            : undefined,
                      }}
                      requiresNames={requiresNames}
                      showCrewId={showCrewId}
                    />
                  ))}
                </Table>
              </Table.Cell>
            </Table.Row>
          );
          combinedGroupCount++;
          i = groupEnd;
          tripsProcessed += groupLength;

          // Check if we need to insert a subtotal row after this combined group
          if (subtotals && subtotalIndex < subtotals.length && tripsProcessed === subtotals[subtotalIndex].count) {
            const subtotal = subtotals[subtotalIndex];
            const isLast = subtotalIndex === subtotals.length - 1;
            result.push(
              <SubtotalRow
                key={`${i}-${subtotalIndex}`}
                subtotal={subtotal.total}
                totalTrips={isLast ? totalTrips : undefined}
                netBillableTrips={isLast ? netBillableTrips : undefined}
                hasDiscounts={isLast ? hasDiscounts : undefined}
              />
            );
            subtotalIndex++;
            tripsProcessed = 0;
          }
          continue;
        }
      }
      result.push(
        <InvoicePdfTableRow
          hasDiscounts={hasDiscounts}
          types={types}
          key={i}
          trip={trip}
          requiresNames={requiresNames}
          showCrewId={showCrewId}
        />
      );
      i++;
      tripsProcessed++;

      // Check if we need to insert a subtotal row after this trip
      if (subtotals && subtotalIndex < subtotals.length && tripsProcessed === subtotals[subtotalIndex].count) {
        const subtotal = subtotals[subtotalIndex];
        const isLast = subtotalIndex === subtotals.length - 1;
        result.push(
          <SubtotalRow
            key={`${subtotalIndex}-${i}`}
            subtotal={subtotal.total}
            totalTrips={isLast ? totalTrips : undefined}
            netBillableTrips={isLast ? netBillableTrips : undefined}
            hasDiscounts={isLast ? hasDiscounts : undefined}
          />
        );
        subtotalIndex++;
        tripsProcessed = 0;
      }
    }
    // Mark the last row with PdfReady when this is the final table on the page (no custom charges)
    if (result.length > 0 && !error && !customCharges?.length) {
      const last = result[result.length - 1];
      result[result.length - 1] = cloneElement(last, {
        className: getClasses(last.props.className, 'LastRow'),
      });
    }
    return [result, combinedGroupCount] as const;
  }, [trips, hasDiscounts, types, requiresNames, showCrewId, subtotals, combineIds, totalTrips, netBillableTrips, customCharges, error]);

  const allCombinedGroupsRendered = totalCombinedGroups === 0 || combinedGroupsRendered >= totalCombinedGroups;
  const pdfReadyValue = useMemo(
    () => ({ notifyCombinedGroupRendered, allCombinedGroupsRendered }),
    [notifyCombinedGroupRendered, allCombinedGroupsRendered]
  );

  return (
    <InvoicePdfReadyContext.Provider value={pdfReadyValue}>
      <Table
        {...props}
        className={getClasses('InvoicePdf-Table', hasDiscounts && 'has-discounts', showCrewId && 'show-crew-id')}
        header={<InvoicePdfTableHeader showCrewId={showCrewId} hasDiscounts={hasDiscounts} />}
      >
        {groupedRows}
      </Table>
    </InvoicePdfReadyContext.Provider>
  );
};

export default InvoicePdfTable;
